import React, { useState, useEffect } from 'react';
import api from '../../api';

function OwnerModal({ owner, onSave, onClose }) {
  const isEdit = !!owner?.id;
  const [form, setForm] = useState(owner || { name: '', email: '', phone: '', payment_method: 'check', management_fee_pct: 20, notes: '' });
  const [saving, setSaving] = useState(false);
  async function handleSave() {
    if (!form.name) return;
    setSaving(true);
    try {
      if (isEdit) await api.put(`/pm/owners/${owner.id}`, form);
      else await api.post('/pm/owners', form);
      onSave();
    } catch {} finally { setSaving(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>{isEdit ? 'Edit Owner' : 'Add Owner'}</div>
        {[
          { label: 'Name *', key: 'name', placeholder: 'John & Jane Smith' },
          { label: 'Email', key: 'email', placeholder: 'owner@email.com', type: 'email' },
          { label: 'Phone', key: 'phone', placeholder: '+1 808-555-0100' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>{f.label}</label>
            <input type={f.type || 'text'} value={form[f.key]} placeholder={f.placeholder}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff' }} />
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Payment Method</label>
            <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(30,41,59,1)', color: '#fff' }}>
              {['check', 'ach', 'zelle', 'venmo', 'wire'].map(v => <option key={v} value={v}>{v.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Mgmt Fee %</label>
            <input type="number" min="0" max="100" step="0.5" value={form.management_fee_pct}
              onChange={e => setForm(p => ({ ...p, management_fee_pct: e.target.value }))}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff' }} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea value={form.notes || ''} rows={2} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.name}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function StatementModal({ owner, properties, onSave, onClose }) {
  const [form, setForm] = useState({ owner_id: owner.id, property_id: '', period_start: '', period_end: '', gross_revenue: '', expenses: '', line_items: [] });
  const [saving, setSaving] = useState(false);
  const gross = parseFloat(form.gross_revenue) || 0;
  const exp = parseFloat(form.expenses) || 0;
  const fee = Math.round(gross * (owner.management_fee_pct / 100) * 100) / 100;
  const net = gross - fee - exp;

  async function handleSave() {
    if (!form.property_id || !form.period_start || !form.period_end) return;
    setSaving(true);
    try { await api.post('/pm/statements', form); onSave(); }
    catch {} finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 460, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Generate Owner Statement</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{owner.name} · {owner.management_fee_pct}% management fee</div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Property *</label>
          <select value={form.property_id} onChange={e => setForm(p => ({ ...p, property_id: e.target.value }))}
            style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(30,41,59,1)', color: '#fff' }}>
            <option value="">— select —</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {[['period_start', 'Period Start'], ['period_end', 'Period End']].map(([k, l]) => (
            <div key={k}>
              <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>{l} *</label>
              <input type="date" value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))}
                style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff' }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Gross Revenue ($)</label>
            <input type="number" value={form.gross_revenue} onChange={e => setForm(p => ({ ...p, gross_revenue: e.target.value }))}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Expenses ($)</label>
            <input type="number" value={form.expenses} onChange={e => setForm(p => ({ ...p, expenses: e.target.value }))}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#fff' }} />
          </div>
        </div>

        {/* Live preview */}
        {gross > 0 && (
          <div style={{ background: 'rgba(13,148,136,.08)', border: '1px solid rgba(13,148,136,.2)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#94a3b8' }}>Gross Revenue</span><span>${gross.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#94a3b8' }}>Management Fee ({owner.management_fee_pct}%)</span><span style={{ color: '#f97316' }}>− ${fee.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#94a3b8' }}>Expenses</span><span style={{ color: '#f97316' }}>− ${exp.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 8, fontWeight: 700 }}>
              <span>Net to Owner</span><span style={{ color: '#0d9488' }}>${net.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.property_id || !form.period_start}>{saving ? 'Saving…' : 'Generate Statement'}</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function OwnersView() {
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [statements, setStatements] = useState([]);
  const [modal, setModal] = useState(null);
  const [stmtModal, setStmtModal] = useState(null);
  const [loading, setLoading] = useState(true);

  function load() {
    Promise.all([api.get('/pm/owners'), api.get('/pm/properties'), api.get('/pm/statements')])
      .then(([o, p, s]) => { setOwners(o.data); setProperties(p.data); setStatements(s.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <span className="page-title">Owners</span>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Add Owner</button>
      </div>
      <div className="page-body">
        {owners.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>No owners yet. Add an owner to generate statements.</div></div>
        ) : (
          <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
            {owners.map(o => (
              <div key={o.id} className="card">
                <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 21, background: 'rgba(99,102,241,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#818cf8', flexShrink: 0 }}>
                    {o.name[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{o.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {o.email && <span>{o.email} · </span>}
                      {o.phone && <span>{o.phone} · </span>}
                      {o.management_fee_pct}% fee · {o.payment_method?.toUpperCase()}
                      {o.property_count > 0 && <span> · {o.property_count} propert{o.property_count !== 1 ? 'ies' : 'y'}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setStmtModal(o)} className="btn btn-sm" style={{ background: 'rgba(13,148,136,.15)', color: '#0d9488', border: 'none' }}>
                      📄 Statement
                    </button>
                    <button onClick={() => setModal(o)} className="btn btn-ghost btn-sm">Edit</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statements history */}
        {statements.length > 0 && (
          <div className="card">
            <div className="card-header">Statement History</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Owner</th><th>Property</th><th>Period</th><th style={{ textAlign: 'right' }}>Gross</th><th style={{ textAlign: 'right' }}>Fee</th><th style={{ textAlign: 'right' }}>Expenses</th><th style={{ textAlign: 'right' }}>Net to Owner</th><th>Status</th></tr></thead>
                <tbody>
                  {statements.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{s.owner_name}</td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{s.property_name}</td>
                      <td style={{ fontSize: 12 }}>
                        {new Date(s.period_start + 'T12:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })} – {new Date(s.period_end + 'T12:00').toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13 }}>${Number(s.gross_revenue).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontSize: 13, color: '#f97316' }}>-${Number(s.management_fee).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontSize: 13, color: '#f97316' }}>-${Number(s.expenses).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#0d9488' }}>${Number(s.net_to_owner).toFixed(2)}</td>
                      <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: s.status === 'sent' ? 'rgba(13,148,136,.15)' : 'rgba(100,116,139,.1)', color: s.status === 'sent' ? '#0d9488' : '#64748b' }}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modal !== null && (
        <OwnerModal owner={Object.keys(modal).length ? modal : null}
          onSave={() => { setModal(null); load(); }} onClose={() => setModal(null)} />
      )}
      {stmtModal && (
        <StatementModal owner={stmtModal} properties={properties.filter(p => p.owner_id === stmtModal.id || !p.owner_id)}
          onSave={() => { setStmtModal(null); load(); }} onClose={() => setStmtModal(null)} />
      )}
    </>
  );
}
